import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, type UseFormRegister, type FieldErrors } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
  Input,
  Button,
  Divider,
  Checkbox,
  RadioGroup,
  RadioGroupItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { AuthShell, PasswordInput } from './components/AuthShell';
import { WafPromoBanner, WAF_GUIDE_URL } from '@/components/promos/WafPromoBanner';
import { TermsDialog } from './terms/TermsDialog';
import { COMPANY_NAME } from './terms/terms-content';
import { brand, brandCopyright } from '@/config/brand';
import { useAuth } from '@/auth/AuthProvider';
import { registerUser } from '@/api/auth-flow';
import { submitWebsiteLead } from '@/api/leads';
import { resolveAuthError } from '@/api/auth-error';
import { trackSignUp } from '@/lib/analytics-events';
import { GoogleSignInButton } from './components/GoogleSignInButton';
import { RegisterOtpStep } from './components/RegisterOtpStep';
import { BusinessLeadRegister } from './BusinessLeadRegister';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AccountType = 'business' | 'individual';

const notice = brand.features.sso ? (
  <Alert tone="info" icon={null}>
    Your account will be created using <strong>{brand.ssoName}</strong> for unified
    access across all {brand.shortName} services with enhanced security features.
  </Alert>
) : null;

const FOOTER = brandCopyright();

function TypeCard({
  value, selected, title, description, onSelect,
}: {
  value: AccountType;
  selected: boolean;
  title: string;
  description: string;
  onSelect: (v: AccountType) => void;
}) {
  return (
    <label
      onClick={() => onSelect(value)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-3) var(--vn-space-4)',
        border: `1px solid ${selected ? 'var(--vn-border-strong)' : 'var(--vn-border-default)'}`,
        borderRadius: 'var(--vn-radius-md)',
        background: selected ? 'var(--vn-bg-subtle)' : 'var(--vn-bg-surface)',
        cursor: 'pointer',
      }}
    >
      <RadioGroupItem value={value} style={{ marginTop: 2 }} />
      <div>
        <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-lg)' }}>{title}</div>
        <div style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)' }}>
          {description}
        </div>
      </div>
    </label>
  );
}

interface FormValues {
  email: string;
  password: string;
  password_confirmation: string;
  contact_person: string;
  phone: string;
  company_name: string;
}

interface FieldHelpers {
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  watch: (name: keyof FormValues) => string;
}

const passwordRules = {
  required: 'Password is required.',
  minLength: { value: 9, message: 'Password must be 9–64 characters.' },
  maxLength: { value: 64, message: 'Password must be 9–64 characters.' },
};

function IndividualFields({ register, errors, watch }: FieldHelpers) {
  return (
    <>
      <Field label="Email" required error={errors.email?.message}>
        {(p) => (
          <Input {...p} type="email" autoComplete="email" invalid={!!errors.email}
            {...register('email', { required: 'Email is required.', pattern: { value: EMAIL_RE, message: 'Email invalid.' } })} />
        )}
      </Field>
      <Field label="Password" required error={errors.password?.message}>
        {(p) => (
          <PasswordInput {...p} autoComplete="new-password" invalid={!!errors.password}
            {...register('password', passwordRules)} />
        )}
      </Field>
      <Field label="Password Confirmation" required error={errors.password_confirmation?.message}>
        {(p) => (
          <PasswordInput {...p} autoComplete="new-password" invalid={!!errors.password_confirmation}
            {...register('password_confirmation', {
              required: 'Please confirm your password.',
              validate: (v) => v === watch('password') || 'Passwords do not match.',
            })} />
        )}
      </Field>
    </>
  );
}

/**
 * Register entry — the account-type chooser + Google. Business accounts go to the
 * B2B lead form (<BusinessLeadRegister>: DataCentral lead + free-email block);
 * individual accounts use the simple email/password form below.
 */
export default function RegisterPage() {
  return <StandardRegister />;
}

function StandardRegister() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState<'type' | 'form' | 'otp'>('type');
  const [accountType, setAccountType] = useState<AccountType>('business');
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      email: '', password: '', password_confirmation: '',
      contact_person: '', phone: '', company_name: '',
    },
  });

  const { mutateAsync } = useMutation({ mutationFn: registerUser });

  // The submitted individual form, held while the user verifies their email.
  const pendingRef = useRef<FormValues | null>(null);

  // Only the individual account type submits here — business registration is a
  // B2B lead form handled by <BusinessLeadRegister> (routed below). Instead of
  // creating the account immediately, hold the values and go to email
  // verification; the account is created only after the OTP is confirmed.
  const onFormSubmit = (values: FormValues) => {
    setError(null);
    pendingRef.current = values;
    setStep('otp');
  };

  // Create the account with the verified OTP. Returns an error message for the
  // OTP step to show, or null on success (then we navigate away).
  const completeRegister = async (otp: string): Promise<string | null> => {
    const values = pendingRef.current;
    if (!values) return 'Something went wrong. Please go back and try again.';
    try {
      const res = await mutateAsync({
        type: 'individual',
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation,
        otp,
      });
      if (res.errors?.length) return res.errors[0].message;
      trackSignUp(accountType);
      // Capture a sales/marketing lead for the individual signup too (mirrors the
      // business flow). referrer + current link are attached inside submitWebsiteLead
      // from the landing attribution, identical to business. Best-effort: the account
      // is already created, so a lead failure must never block auto sign-in.
      void submitWebsiteLead({ email: values.email }).catch(() => {});
      // If the backend returns a session, sign in; otherwise send to login.
      // SSO registration returns Keycloak tokens — persist the real auth_method
      // so token refresh uses the right endpoint (matches InvitationPage).
      if (res.data?.access_token && setSession(res.data, res.data.auth_method ?? 'local')) {
        navigate('/', { replace: true });
      } else {
        navigate('/auth/login', { replace: true });
      }
      return null;
    } catch (e) {
      return resolveAuthError(e, 'Registration failed. Please try again.');
    }
  };

  const helpers: FieldHelpers = { register, errors, watch: (n) => watch(n) };

  // Split sign-up layout: the shared WAAP promo banner fills one half of the block.
  // `flush` drops the banner's own chrome; the CTA (and the banner) open the WAF-free
  // guide in a new tab.
  const promo = brand.promos.wafFree ? (
    <WafPromoBanner
      flush
      ctaLabel="See the 3-step setup"
      onActivate={() => window.open(WAF_GUIDE_URL, '_blank', 'noopener,noreferrer')}
    />
  ) : undefined;

  // Business account → the B2B lead form (captures a DataCentral lead + enforces
  // the free-email block). Individual continues in the simple form below.
  if (step === 'form' && accountType === 'business') {
    return <BusinessLeadRegister onBack={() => setStep('type')} />;
  }

  // Email verification — the individual account is created only after the OTP is
  // confirmed, so fake/unowned emails can't complete signup.
  if (step === 'otp') {
    return (
      <AuthShell
        eyebrow="— verify email"
        title="Verify your email"
        subtitle={pendingRef.current?.email}
        footer={FOOTER}
        splitPane={promo}
      >
        <RegisterOtpStep
          email={pendingRef.current?.email ?? ''}
          onVerify={completeRegister}
          onBack={() => {
            setError(null);
            setStep('form');
          }}
        />
      </AuthShell>
    );
  }

  if (step === 'type') {
    return (
      <AuthShell title={`Sign up to ${brand.legalName}`} footer={FOOTER} splitPane={promo}>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {notice}
          {error ? <Alert tone="danger" title={error} /> : null}
          <RadioGroup
            value={accountType}
            onValueChange={(v) => setAccountType(v as AccountType)}
            style={{ gap: 'var(--vn-space-3)' }}
          >
            <TypeCard value="business" selected={accountType === 'business'}
              title="Business Account"
              description="Take advantage of our comprehensive solutions and dedicated support to drive business success."
              onSelect={setAccountType} />
            <TypeCard value="individual" selected={accountType === 'individual'}
              title="Individual Account"
              description="For individual needs and personal use."
              onSelect={setAccountType} />
          </RadioGroup>

          <label style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'flex-start', fontSize: 'var(--vn-font-size-md)' }}>
            <Checkbox
              checked={agreed}
              onCheckedChange={(v) => {
                // Agreement is only granted by reading the Terms to the end.
                if (v) setTermsOpen(true);
                else setAgreed(false);
              }}
            />
            <span>
              By clicking, you acknowledge and agree to the {COMPANY_NAME}{' '}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                style={{
                  border: 'none', background: 'none', padding: 0, font: 'inherit',
                  color: 'var(--vn-primary-text)', textDecoration: 'underline', cursor: 'pointer',
                }}
              >
                Terms of Service
              </button>
              .
            </span>
          </label>

          <Button fullWidth disabled={!agreed} onClick={() => setStep('form')}>
            Next
          </Button>

          {brand.features.sso && (
            <>
              <Divider>Or</Divider>
              <GoogleSignInButton redirectTo="/" onError={setError} />
            </>
          )}

          <TermsDialog
            open={termsOpen}
            onOpenChange={setTermsOpen}
            onAgree={() => {
              setAgreed(true);
              setTermsOpen(false);
            }}
          />

          <Divider>Already a member?</Divider>

          <Button asChild variant="secondary" fullWidth>
            <Link to="/auth/login">Login</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="— individual" title={`Sign up to ${brand.legalName}`} footer={FOOTER} splitPane={promo}>
      <form style={{ display: 'grid', gap: 'var(--vn-space-4)' }} onSubmit={handleSubmit(onFormSubmit)}>
        {notice}
        {error ? <Alert tone="danger" title={error} /> : null}
        <IndividualFields {...helpers} />

        <Button type="submit" fullWidth>
          Continue
        </Button>
        <Button type="button" variant="secondary" fullWidth onClick={() => setStep('type')}>
          Back
        </Button>
        <Divider>Already have account?</Divider>
        <Button asChild type="button" variant="secondary" fullWidth>
          <Link to="/auth/login">Customer Login</Link>
        </Button>
      </form>
    </AuthShell>
  );
}
